import React from "react";
import { useNavigate } from "react-router-dom";
import { CoreModal, CoreButton } from "./komodo";
import { useQuiz } from "../context/QuizContext";
import { strings } from "../data/questionGenerator";
import "./PreferencesModal.scss";

/**
 * PreferencesModal Component
 * Recreated from Angular master branch resultsPreferences.route.html and resultsPreferences.route.js
 * Displays user's quiz answers in a modal with edit functionality
 */
const PreferencesModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { answers, clearAllAnswers } = useQuiz();

  // Get preference strings (matching Angular vm.getString function)
  const getPreferenceString = (key) => {
    const strings = {
      title: "Edit preferences",
      edit: "Edit",
      start_over: "Start over",
      confirm: "Confirm",
    };
    return strings[key] || key;
  };

  // Get quiz strings (matching Angular vm.getQuizString function)
  const getQuizString = (key) => {
    // Extract question key from the key (e.g., "q1.label" -> "q1")
    const [questionKey, property] = key.split(".");
    if (property === "label" && strings.quiz[questionKey]) {
      return strings.quiz[questionKey].label;
    }
    return key;
  };

  // Get question option string using questionDefinitions (matches quiz option creation logic)
  const { questionDefinitions } = require("../data/questionGenerator");
  const getQuestionOptionString = (question, key) => {
    if (!key) return null;

    // Check if this is a skipped answer
    const def = questionDefinitions[question];
    if (def && def.skip_value && key === def.skip_value) {
      return "Unsure"; // Display "Unsure" for skipped questions
    }

    if (!def) return "";
    // For q4, pass answers as state for dynamic label selection
    let options;
    if (def.getOptions) {
      // For q4, pass answers as state for dynamic label selection
      options = def.getOptions(answers);
    } else if (def.getBaseOptions) {
      options = def.getBaseOptions();
    } else {
      return "";
    }
    const found = options.find((opt) => opt.key === key);
    return found && found.getLabel ? found.getLabel() : "";
  };
  const goToQuestion = (question) => {
    onClose();
    // Scroll to top before navigating
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/quiz?goTo=${question}`);
  };

  // Start over function (matching Angular vm.startOver function)
  const startOver = () => {
    clearAllAnswers();
    onClose();
    navigate("/quiz");
  };

  // Convert answers object to array for display (matching Angular ng-repeat structure)
  const questionOrder = ["q1", "q2", "q3", "q4", "q5", "q6"];

  const answersArray = questionOrder
    .filter(
      (question) => answers[question] && answers[question].length > 0
      // Show all questions including skipped ones (which have skip_value like "unsure")
    )
    .map((question) => [question, answers[question]]);

  if (!isOpen) return null;

  return (
    <CoreModal
      className="tf-results-preferences"
      isopen={isOpen}
      onModalClosed={onClose}
      bgclose="true"
      closeicon="true"
      customcontent="true"
      width="720px"
      nooverflow="false"
    >
      <div
        slot="custom-content"
        className="tf-modal-content tf-mobile-full-screen"
      >
        <div className="tf-modal-header">
          <h2>{getPreferenceString("title")}</h2>
        </div>
        <div className="tf-modal-body">
          <ul>
            {answersArray.map(([question, values]) => (
              <li key={question}>
                <div className="tf-answer">
                  <h6>{getQuizString(`${question}.label`)}</h6>
                  <div>
                    {values
                      .map((value) => getQuestionOptionString(question, value))
                      .filter((displayValue) => displayValue)
                      .join(", ")}
                  </div>
                </div>

                <span
                  className="tf-link tf-edit"
                  onClick={() => goToQuestion(question)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      goToQuestion(question);
                    }
                  }}
                >
                  {getPreferenceString("edit")}
                  <i className="tf-komodo-24-icon-edit-default-mono"></i>
                </span>
              </li>
            ))}
          </ul>

          <div className="tf-modal-buttons">
            <CoreButton
              onClick={startOver}
              variant="information"
              size="small"
              type="button"
              className="tf-btn-start-over"
            >
              <span slot="content">{getPreferenceString("start_over")}</span>
            </CoreButton>
            <CoreButton
              onClick={onClose}
              variant="primary"
              size="small"
              type="button"
              className="tf-btn-confirm"
            >
              <span slot="content">{getPreferenceString("confirm")}</span>
            </CoreButton>
          </div>
        </div>
      </div>
    </CoreModal>
  );
};

export default PreferencesModal;
